import type { CSSProperties, ReactNode } from 'react';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@monksflow/monks-ui';

/**
 * An empty notice drawn the way the design system draws one.
 *
 * The app-wide `EmptyState` steps its title up to 24px and hoists the icon badge out of the
 * header, which puts the badge 32px from a title 8px from its own description -- a loose icon
 * above a block rather than the head of it. This library follows the design system instead:
 * a 16px title and one gap between every item, the badge included.
 *
 * Local rather than a fix to `EmptyState` because that component is the title of some eighty
 * notices across the app, and its size is not this screen's to change. Fold this back in if the
 * house component is ever brought in line.
 */
export const LibraryEmpty = ({
  icon,
  title,
  description,
  descriptionMaxWidth,
  className,
  role = 'status',
  children,
  'data-testid': testId,
}: {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** The design system leaves a description unmeasured, so a long one runs the full width. */
  descriptionMaxWidth?: CSSProperties['maxWidth'];
  className?: string;
  /** `alert` for a notice that reports a failure rather than an absence. */
  role?: 'status' | 'alert';
  /** Action slot, for the notices that can offer a way out of what they describe. */
  children?: ReactNode;
  'data-testid'?: string;
}) => (
  <Empty role={role} data-testid={testId} className={className}>
    <EmptyHeader>
      <EmptyMedia variant="icon">{icon}</EmptyMedia>

      {/* `EmptyTitle` is a bare `div`, so without this the notice is missing from a screen
          reader's list of headings. Level 2: the page header above it is the `h1`. */}
      <EmptyTitle role="heading" aria-level={2}>
        {title}
      </EmptyTitle>

      {description !== undefined && (
        <EmptyDescription style={{ maxWidth: descriptionMaxWidth }}>{description}</EmptyDescription>
      )}
    </EmptyHeader>

    {children !== undefined && <EmptyContent>{children}</EmptyContent>}
  </Empty>
);
